/**
 * Utility per la navigazione e menu dell'applicazione
 * Centralizza la logica di routing per garantire consistenza tra desktop e mobile
 */

export interface NavItem {
  icon: string;
  label: string;
  path: string;
}

// Route permesse per ogni ruolo (da App.tsx)
export const ROLE_ROUTES = {
  admin: [
    '/cruscotto', '/admin', '/admin/documents', '/admin/audit-log', '/applications', '/valuta-inquilini',
    '/profile', '/documents', '/consents', '/notifiche', '/messaggi', '/proprieta', '/proprieta/nuova', '/landlord-search',
    '/candidature', '/recensioni', '/contracts', '/kpi-ranking', '/assistenza'
  ],
  tenant: [
    '/cruscotto', '/profile', '/documents', '/consents', '/landlord-search',
    '/notifiche', '/messaggi', '/candidature', '/recensioni', '/contracts', '/assistenza'
  ],
  landlord: [
    '/cruscotto', '/profile', '/documents', '/consents', '/notifiche', '/messaggi', '/landlord-search',
    '/proprieta', '/proprieta/nuova', '/candidature', '/recensioni', '/contracts', '/assistenza'
  ],
  agency: [
    '/cruscotto', '/tenant-search', '/landlord-search', '/profile', '/documents', '/consents',
    '/notifiche', '/messaggi', '/proprieta', '/proprieta/nuova', '/candidature', '/recensioni', '/contracts', '/kpi-ranking', '/assistenza'
  ],
  guest: ['/tenant-search', '/landlord-search']
} as const;

// Mappatura route -> elementi di menu
const ROUTE_NAV_MAPPING: Record<string, Omit<NavItem, 'path'>> = {
  '/cruscotto': { icon: 'dashboard', label: 'Cruscotto' },
  '/admin': { icon: 'admin', label: 'Admin' },
  '/admin/documents': { icon: 'docs', label: 'Verifica documenti' },
  '/admin/audit-log': { icon: 'audit', label: 'Audit Log' },
  '/candidature': { icon: 'docs', label: 'Candidature' },
  '/recensioni': { icon: 'review', label: 'Recensioni' },
  '/proprieta': { icon: 'landlord', label: 'Immobili' },
  '/proprieta/nuova': { icon: 'plus', label: 'Nuovo immobile' },
  '/tenant-search': { icon: 'tenant', label: 'Inquilini' },
  '/landlord-search': { icon: 'landlord', label: 'Proprietari' },
  '/profile': { icon: 'user', label: 'Profilo' },
  '/contracts': { icon: 'docs', label: 'Contratti' },
  '/kpi-ranking': { icon: 'starfull', label: 'KPI Ranking' },
  '/documents': { icon: 'docs', label: 'Documenti' },
  '/consents': { icon: 'settings', label: 'Consensi' },
  '/notifiche': { icon: 'alert', label: 'Notifiche' },
  '/messaggi': { icon: 'message', label: 'Messaggi' },
  '/assistenza': { icon: 'help', label: 'Assistenza' }
};

/**
 * Ottiene gli elementi di navigazione per un ruolo specifico (o array di ruoli)
 * Filtra le route permesse e le converte in elementi di menu
 * Se vengono passati ruoli multipli, combina le route permesse
 */
export function getNavItemsForRole(role: string | string[] | undefined): NavItem[] {
  if (!role) return [];

  // Gestisci ruoli multipli
  const roles = Array.isArray(role) ? role : [role];
  const allowedRoutes = new Set<string>();

  roles.forEach(r => {
    if (ROLE_ROUTES[r as keyof typeof ROLE_ROUTES]) {
      const routes = ROLE_ROUTES[r as keyof typeof ROLE_ROUTES];
      routes.forEach(route => allowedRoutes.add(route));
    }
  });

  // Filtra solo le route che hanno una mappatura nel menu
  const menuRoutes = Array.from(allowedRoutes).filter(route => ROUTE_NAV_MAPPING[route]);

  // Converte le route in elementi di menu
  return menuRoutes.map(route => ({
    ...ROUTE_NAV_MAPPING[route],
    path: route
  }));
}

export function getGuestNavItems(): NavItem[] {
  return [
    { icon: 'tenant', label: 'Cerca affittuari', path: '/tenant-search' },
    { icon: 'landlord', label: 'Cerca proprietari', path: '/landlord-search' },
  ];
}

/**
 * Ottiene gli elementi di navigazione esclusi dalla navigazione mobile principale
 * Utile per i menu a tendina che non devono duplicare gli elementi già visibili
 * Per utenti con ruoli multipli, usa la logica del ruolo primario (landlord se presente, altrimenti tenant)
 */
export function getNavItemsExcludingMobileMain(role: string | string[] | undefined): NavItem[] {
  const allItems = getNavItemsForRole(role);

  if (!role) return allItems;

  // Determina il ruolo primario per la navigazione mobile
  const roles = Array.isArray(role) ? role : [role];
  const primaryRole = roles.includes('landlord') ? 'landlord' : 
                     roles.includes('tenant') ? 'tenant' : 
                     roles[0];

  // Elementi da escludere dalla navigazione mobile principale
  const excludedPaths = primaryRole === 'tenant'
    ? ['/notifiche', '/candidature', '/cruscotto']
    : primaryRole === 'landlord'
    ? ['/notifiche', '/candidature', '/tenant-search', '/cruscotto']
    : [];

  return allItems.filter(item => !excludedPaths.includes(item.path));
}

/**
 * Ottiene gli elementi di navigazione per l'header desktop (principali)
 * Logica flessibile che può adattarsi alle dimensioni schermo o zoom
 */
export function getNavItemsForDesktopHeader(role: string | undefined): NavItem[] {
  if (role === 'guest') {
    return getGuestNavItems();
  }

  const allItems = getNavItemsForRole(role);

  if (!role) return [];

  // Header primario essenziale: massimo 5 voci
  const maxHeaderItems = 5;

  // Priorità degli elementi per l'header (da più a meno importante)
  const priorityOrder = role === 'admin'
    ? [
      '/admin',
      '/cruscotto',
      '/candidature',
      '/contracts',
      '/recensioni',
      '/proprieta',
      '/landlord-search',
      '/tenant-search',
      '/kpi-ranking',
      '/assistenza',
    ]
    : role === 'tenant'
    ? [
      '/cruscotto',
      '/landlord-search',
      '/candidature',
      '/contracts',
      '/recensioni',
      '/assistenza',
    ]
    : role === 'landlord' || role === 'agency'
    ? [
      '/cruscotto',
      '/tenant-search',
      '/candidature',
      '/contracts',
      '/recensioni',
      '/proprieta',
      '/assistenza',
    ]
    : [
      '/cruscotto',
      '/tenant-search',
      '/landlord-search',
      '/candidature',
      '/contracts',
      '/recensioni',
      '/assistenza',
    ];

  // Crea lista ordinata per priorità
  const prioritizedItems: NavItem[] = [];
  const remainingItems: NavItem[] = [];

  priorityOrder.forEach(path => {
    const item = allItems.find(i => i.path === path);
    if (item) {
      prioritizedItems.push(item);
    }
  });

  // Aggiungi eventuali elementi non nella lista priorità
  allItems.forEach(item => {
    if (!prioritizedItems.find(p => p.path === item.path)) {
      remainingItems.push(item);
    }
  });

  // Combina e limita al numero massimo
  const headerItems = [...prioritizedItems, ...remainingItems].slice(0, maxHeaderItems);

  return headerItems;
}

/**
 * Ottiene gli elementi di navigazione per il dropdown dell'header desktop
 * Esclude automaticamente gli elementi già presenti nell'header
 */
export function getNavItemsForDesktopDropdown(role: string | undefined): NavItem[] {
  const allItems = getNavItemsForRole(role);
  const headerItems = getNavItemsForDesktopHeader(role);

  // Escludi gli elementi già presenti nell'header
  const headerPaths = headerItems.map(item => item.path);

  return allItems.filter(item => !headerPaths.includes(item.path));
}

/**
 * Verifica se un ruolo (o array di ruoli) può accedere a una route specifica
 */
export function canAccessRoute(role: string | string[] | undefined, path: string): boolean {
  if (!role) return false;

  const roles = Array.isArray(role) ? role : [role];

  return roles.some(r => {
    if (!ROLE_ROUTES[r as keyof typeof ROLE_ROUTES]) {
      return false;
    }

    const allowedRoutes = ROLE_ROUTES[r as keyof typeof ROLE_ROUTES] as readonly string[];
    return allowedRoutes.includes(path);
  });
}